import { useCallback, useEffect, useRef } from 'react'
import { useMotionValue, useReducedMotion, animate } from 'framer-motion'
import { springGentle } from '@/lib/motionConfig'

interface UseEdgeBackSwipeOptions {
  /** 뒤로가기 동작 (보통 navigate(-1)) */
  onBack: () => void
  /** 좌측 에지 감지 폭 (px, 기본 24) */
  edgeWidth?: number
  /** 뒤로가기로 판정할 수평 이동 임계 (px, 기본 70) */
  threshold?: number
  /** 비활성화 */
  disabled?: boolean
}

/**
 * 좌측 에지 백-스와이프 훅 (PWA standalone 모드의 누락된 가로 뒤로가기 보완).
 *
 * 화면 좌측 24px 이내에서 시작한 터치를 추적해, 우측으로 `threshold` 이상
 * 끌면 `onBack`을 호출한다. 끄는 동안 페이지 래퍼가 `translateX`로 살짝 따라와
 * 어포던스를 주고, 취소 시 스프링으로 복귀한다.
 *
 * 가드:
 * - coarse 포인터(터치)에서만 활성화 (matchMedia).
 * - 에지 24px 밖에서 시작하면 무시 → 가로 스크롤 영역/채팅 답장 스와이프와 충돌 회피.
 * - 우세축 판정: 수평 이동이 수직보다 클 때만 추종.
 *
 * 반환 `x`(MotionValue)를 페이지 루트의 `style={{ x }}`에 연결한다.
 */
export function useEdgeBackSwipe({
  onBack,
  edgeWidth = 24,
  threshold = 70,
  disabled = false,
}: UseEdgeBackSwipeOptions) {
  const shouldReduceMotion = useReducedMotion()
  const x = useMotionValue(0)

  const startX = useRef(0)
  const startY = useRef(0)
  const active = useRef(false)
  const axisLocked = useRef<'horizontal' | 'vertical' | null>(null)

  // coarse 포인터 + 비활성 플래그 기준으로 부착 여부 결정
  const enabled =
    !disabled &&
    !shouldReduceMotion &&
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(pointer: coarse)').matches

  const settleBack = useCallback(() => {
    animate(x, 0, springGentle)
  }, [x])

  const onTouchStart = useCallback(
    (e: React.TouchEvent) => {
      if (!enabled || e.touches.length !== 1) return
      const t = e.touches[0]
      // 좌측 에지 영역에서 시작한 경우만 추적
      if (t.clientX > edgeWidth) {
        active.current = false
        return
      }
      active.current = true
      axisLocked.current = null
      startX.current = t.clientX
      startY.current = t.clientY
      x.stop()
    },
    [enabled, edgeWidth, x]
  )

  const onTouchMove = useCallback(
    (e: React.TouchEvent) => {
      if (!active.current) return
      const t = e.touches[0]
      const dx = t.clientX - startX.current
      const dy = t.clientY - startY.current

      // 우세축 판정 (10px 이동 후 잠금)
      if (axisLocked.current === null) {
        if (Math.abs(dx) > 10 || Math.abs(dy) > 10) {
          axisLocked.current = Math.abs(dx) > Math.abs(dy) ? 'horizontal' : 'vertical'
        } else {
          return
        }
      }
      if (axisLocked.current !== 'horizontal') {
        // 세로 제스처 — 추적 종료, 스크롤 허용
        active.current = false
        x.set(0)
        return
      }

      // 오른쪽으로만 추종 (저항 적용), 좌측은 0 고정
      const followed = dx > 0 ? Math.min(dx * 0.5, 160) : 0
      x.set(followed)
    },
    [x]
  )

  const onTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      if (!active.current) {
        settleBack()
        return
      }
      active.current = false
      const dx = (e.changedTouches[0]?.clientX ?? startX.current) - startX.current
      if (axisLocked.current === 'horizontal' && dx > threshold) {
        onBack()
      }
      settleBack()
      axisLocked.current = null
    },
    [threshold, onBack, settleBack]
  )

  // 언마운트 시 위치 리셋
  useEffect(() => {
    return () => {
      x.set(0)
    }
  }, [x])

  // 비활성 시 빈 핸들러 → 네이티브 동작 그대로
  const swipeHandlers = enabled
    ? { onTouchStart, onTouchMove, onTouchEnd }
    : {}

  return {
    /** 페이지 루트 style={{ x }} 에 연결할 MotionValue */
    x,
    /** 페이지 루트에 스프레드할 터치 핸들러 */
    swipeHandlers,
    /** 활성 여부 (조건부 motion.div 사용 판단용) */
    enabled,
  }
}
